// src/phase4/systems/EnemySystem.ts
// Enemy logic management system

import Phaser from 'phaser';
import type { EnemyInstance } from '../types/enemy';
import type { ProjectileData } from './TowerSystem';

export interface EnemyDeathCallback {
  (enemyId: string, bounty: number): void;
}

export interface EnemyEscapeCallback {
  (enemyId: string, damage: number): void;
}

export interface ProjectileHitCallback {
  (projectile: ProjectileData, enemyId: string, damageDealt: number): void;
}

export class EnemySystem {
  private scene: Phaser.Scene;
  private path: Phaser.Curves.Path;

  // Callbacks
  private onDeath?: EnemyDeathCallback;
  private onEscape?: EnemyEscapeCallback;
  private onHit?: ProjectileHitCallback;

  // Enemy tracking
  private enemies: Map<string, EnemyInstance> = new Map();

  constructor(scene: Phaser.Scene, path: Phaser.Curves.Path) {
    this.scene = scene;
    this.path = path;
  }

  // Callback setters
  setDeathCallback(callback: EnemyDeathCallback): void {
    this.onDeath = callback;
  }

  setEscapeCallback(callback: EnemyEscapeCallback): void {
    this.onEscape = callback;
  }

  setHitCallback(callback: ProjectileHitCallback): void {
    this.onHit = callback;
  }

  // Register enemy (called when spawned)
  registerEnemy(enemy: EnemyInstance): void {
    this.enemies.set(enemy.id, enemy);
    console.log(`[EnemySystem] Registered enemy ${enemy.id} (${enemy.type})`);
  }

  // Main update loop
  update(delta: number): void {
    const enemiesToRemove: string[] = [];

    for (const [id, enemy] of this.enemies) {
      if (enemy.isDead) {
        enemiesToRemove.push(id);
        continue;
      }

      // Calculate speed with any active modifiers
      let currentSpeed = enemy.speed;
      if (enemy.abilityState?.type === 'speed_boost' && enemy.abilityState.expiresAt) {
        if (this.scene.time.now > enemy.abilityState.expiresAt) {
          enemy.abilityState.isActive = false;
        } else {
          currentSpeed *= 1.5; // 50% speed boost
        }
      }

      // Move along path
      enemy.pathProgress += currentSpeed * (delta / 16); // Normalize to ~60fps

      // Update position
      const point = this.path.getPoint(Math.min(enemy.pathProgress, 1));
      if (point) {
        enemy.x = point.x;
        enemy.y = point.y;
      }

      // Check if reached end
      if (enemy.pathProgress >= 1) {
        this.onEscape?.(id, enemy.maxHp); // Deal max HP as damage to base
        enemiesToRemove.push(id);
      }
    }

    // Remove processed enemies
    for (const id of enemiesToRemove) {
      this.enemies.delete(id);
    }
  }

  // Apply projectile damage
  applyDamage(projectile: ProjectileData): { damageDealt: number; enemyKilled: boolean } {
    const enemy = this.enemies.get(projectile.targetId);
    if (!enemy || enemy.isDead) {
      return { damageDealt: 0, enemyKilled: false };
    }

    // Check shield
    let actualDamage = projectile.damage;
    if (enemy.abilityState?.type === 'shield' && enemy.abilityState.triggerValue) {
      const shieldAmount = enemy.abilityState.triggerValue;
      if (shieldAmount > 0) {
        const absorbed = Math.min(shieldAmount, actualDamage);
        enemy.abilityState.triggerValue! -= absorbed;
        actualDamage -= absorbed;

        if (enemy.abilityState.triggerValue <= 0) {
          enemy.abilityState.isActive = false;
        }
      }
    }

    // Apply damage
    enemy.currentHp -= actualDamage;

    // Apply slow effect
    if (projectile.hasSlowEffect) {
      // Apply slow directly to speed (temporary)
      enemy.speed *= 0.5;
    }

    // Check for stealth break
    if (enemy.isStealthed && actualDamage > 0) {
      enemy.isStealthed = false;
    }

    // Check death
    const killed = enemy.currentHp <= 0;
    if (killed) {
      enemy.isDead = true;
      this.onDeath?.(projectile.targetId, this.getBounty(enemy));
    }

    this.onHit?.(projectile, projectile.targetId, actualDamage);

    return { damageDealt: actualDamage, enemyKilled: killed };
  }

  private getBounty(enemy: EnemyInstance): number {
    // Base bounty by type
    const baseBounties: Record<string, number> = {
      [enemy.type === 'SQL_INJECTION' ? 'SQL_INJECTION' : '']: 15,
      [enemy.type === 'XSS_ATTACK' ? 'XSS_ATTACK' : '']: 20,
      [enemy.type === 'DDOS_BOT' ? 'DDOS_BOT' : '']: 30,
      [enemy.type === 'TROJAN' ? 'TROJAN' : '']: 35,
      [enemy.type === 'ZERO_DAY' ? 'ZERO_DAY' : '']: 100,
    };

    return baseBounties[enemy.type] || 15;
  }

  // Getters
  getEnemy(id: string): EnemyInstance | undefined {
    return this.enemies.get(id);
  }

  getAllEnemies(): EnemyInstance[] {
    return Array.from(this.enemies.values());
  }

  getActiveEnemies(): EnemyInstance[] {
    return Array.from(this.enemies.values()).filter(e => !e.isDead);
  }

  getEnemyCount(): number {
    return this.enemies.size;
  }

  // Reset
  reset(): void {
    this.enemies.clear();
  }
}
